<template>
  <b-card no-body>
    <!-- Header: filters + sync button -->
    <b-card-body>
      <b-row>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Provider</label>
          <b-form-select
            v-model="dataQuery.providerId"
            :options="providerOptions"
            @change="onFilterChange"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Market</label>
          <b-form-select
            v-model="dataQuery.market"
            :options="marketOptions"
            @change="onFilterChange"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Arama (sembol / asset)</label>
          <b-form-input
            v-model="dataQuery.search"
            placeholder="BTC, ETH..."
            @input="onSearchInput"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="d-flex align-items-end mb-1"
        >
          <b-form-checkbox
            v-model="dataQuery.activeOnly"
            switch
            class="mr-2"
            @change="onFilterChange"
          >
            Sadece aktif
          </b-form-checkbox>
          <b-button
            variant="primary"
            class="ml-auto"
            :disabled="syncing"
            @click="openSyncModal"
          >
            <FeatherIcon
              icon="RefreshCwIcon"
              :class="{ 'spin': syncing }"
            /> Senkronize Et
          </b-button>
        </b-col>
      </b-row>
    </b-card-body>

    <!-- Table -->
    <b-table
      responsive="sm"
      :fields="fields"
      :items="dataList"
      :busy="loading"
      striped
      show-empty
      empty-text="Coin bulunamadı. 'Senkronize Et' ile Binance'dan çekebilirsiniz."
    >
      <template #cell(symbol)="data">
        <div class="font-weight-bold">
          {{ data.item.symbol }}
        </div>
        <div class="font-small-3 text-muted">
          {{ data.item.baseAsset }} / {{ data.item.quoteAsset }}
        </div>
      </template>
      <template #cell(market)="data">
        <b-badge :variant="data.item.market === 'FUTURES' ? 'info' : 'success'">
          {{ data.item.market }}
        </b-badge>
      </template>
      <template #cell(minQty)="data">
        <span class="font-small-3">{{ data.item.minQty || '-' }}</span>
      </template>
      <template #cell(tickSize)="data">
        <span class="font-small-3">{{ data.item.tickSize || '-' }}</span>
      </template>
      <template #cell(lastSyncedAt)="data">
        <span class="font-small-3 text-muted">
          {{ formatDate(data.item.lastSyncedAt) }}
        </span>
      </template>
      <template #cell(isActive)="data">
        <b-form-checkbox
          :checked="data.item.isActive"
          switch
          @change="onToggleActive(data.item.id, $event)"
        />
      </template>
      <template #table-busy>
        <div class="text-center py-2">
          <b-spinner small /> Yükleniyor...
        </div>
      </template>
    </b-table>

    <!-- Pagination -->
    <b-card-footer>
      <b-row align-v="center">
        <b-col
          cols="12"
          md="6"
        >
          <small class="text-muted">
            Toplam {{ metaData.total }} coin (Sayfa {{ metaData.page }} / {{ metaData.totalPages || 1 }})
          </small>
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="text-right"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="metaData.total"
            :per-page="dataQuery.limit"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            class="mb-0"
            @input="onPageChange"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </b-col>
      </b-row>

      <!-- Sync result panel -->
      <div
        v-if="syncResult && syncResult.length"
        class="mt-2 p-1 bg-light-success rounded"
      >
        <strong class="text-success">Son sync sonucu:</strong>
        <ul class="mb-0 mt-1 font-small-3">
          <li
            v-for="(r, idx) in syncResult"
            :key="idx"
          >
            {{ r.providerKind }} / {{ r.market }}: +{{ r.upserted }} eklendi, -{{ r.deactivated }} pasif ({{ r.durationMs }}ms)
          </li>
        </ul>
      </div>
    </b-card-footer>

    <!-- ─── Sync Onay Modal ─────────────────────────────────────────────── -->
    <b-modal
      v-model="syncModal.show"
      title="Coin Senkronizasyonu — Onay"
      size="md"
      hide-footer
      no-close-on-backdrop
    >
      <b-alert
        show
        variant="warning"
        class="py-1 px-1 mb-2"
      >
        <small>
          <feather-icon icon="AlertTriangleIcon" />
          <strong>Dikkat:</strong> Binance'de <strong>USDT ve USDC ayrı cüzdanlardır</strong>.
          Sadece kullanmak istediğin quote asset'leri seç — diğerleri DB'ye çekilmez ve
          mevcut listede varsa pasif yapılır.
        </small>
      </b-alert>
      <label class="font-small-3 text-muted mb-1 d-block">Quote Asset Listesi (en az 1 seç) *</label>
      <div class="quote-checkbox-grid">
        <b-form-checkbox
          v-for="q in quoteOptions"
          :key="q"
          v-model="syncModal.quoteAssets"
          :value="q"
          class="mb-1"
        >
          <strong>{{ q }}</strong>
          <small
            v-if="q === 'USDT'"
            class="text-muted ml-1"
          >(en yaygın futures stablecoin)</small>
          <small
            v-else-if="q === 'USDC'"
            class="text-muted ml-1"
          >(USDT'den ayrı cüzdan)</small>
          <small
            v-else-if="q === 'FDUSD'"
            class="text-muted ml-1"
          >(yeni stablecoin)</small>
          <small
            v-else-if="q === 'BTC' || q === 'ETH'"
            class="text-muted ml-1"
          >({{ q }}-margined çiftler)</small>
        </b-form-checkbox>
      </div>
      <small class="text-muted">
        Şu an seçili: <strong>{{ syncModal.quoteAssets.join(', ') || '— hiçbiri —' }}</strong>
      </small>
      <div class="text-right mt-2">
        <b-button
          variant="secondary"
          class="mr-1"
          @click="syncModal.show = false"
        >
          İptal
        </b-button>
        <b-button
          variant="primary"
          :disabled="!syncModal.quoteAssets.length || syncing"
          @click="onConfirmSync"
        >
          <b-spinner
            v-if="syncing"
            small
          />
          <feather-icon
            v-else
            icon="RefreshCwIcon"
          /> {{ syncing ? 'Senkronize ediliyor...' : 'Senkronize Et' }}
        </b-button>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard, BCardBody, BCardFooter, BTable, BPagination, BButton, BFormSelect,
  BFormInput, BFormCheckbox, BRow, BCol, BBadge, BSpinner, BModal, BAlert,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'CoinsIndex',
  components: {
    BCard,
    BCardBody,
    BCardFooter,
    BTable,
    BPagination,
    BButton,
    BFormSelect,
    BFormInput,
    BFormCheckbox,
    BRow,
    BCol,
    BBadge,
    BSpinner,
    BModal,
    BAlert,
  },
  data() {
    return {
      currentPage: 1,
      loading: false,
      syncing: false,
      searchTimer: null,
      // ─── Sync onay modal ────────────────────────────────────────────
      syncModal: { show: false, quoteAssets: ['USDT'] },
      quoteOptions: ['USDT', 'USDC', 'FDUSD', 'BTC', 'ETH'],
      providerOptions: [
        { value: 1, text: 'Binance (live)' },
        { value: 2, text: 'Binance Testnet' },
        { value: 3, text: 'Simulated' },
      ],
      marketOptions: [
        { value: undefined, text: 'Tümü' },
        { value: 'FUTURES', text: 'Futures' },
        { value: 'SPOT', text: 'Spot' },
      ],
      fields: [
        {
          key: 'id', label: '#', thClass: 'width-50', tdClass: 'width-50',
        },
        { key: 'symbol', label: 'Sembol' },
        {
          key: 'market', label: 'Market', thClass: 'width-100 text-center', tdClass: 'width-100 text-center',
        },
        {
          key: 'minQty', label: 'Min Qty', thClass: 'width-100', tdClass: 'width-100',
        },
        {
          key: 'tickSize', label: 'Tick Size', thClass: 'width-100', tdClass: 'width-100',
        },
        {
          key: 'lastSyncedAt', label: 'Son Sync', thClass: 'width-150', tdClass: 'width-150',
        },
        {
          key: 'isActive', label: 'Aktif', thClass: 'width-80 text-center', tdClass: 'width-80 text-center',
        },
      ],
      dataQuery: {
        providerId: 1,
        market: undefined,
        search: '',
        activeOnly: true,
        page: 1,
        limit: 50,
      },
    }
  },
  computed: {
    dataList() { return this.$store.getters['coins/dataList'] },
    metaData() { return this.$store.getters['coins/metaData'] },
    syncResult() { return this.$store.getters['coins/syncResult'] },
    saveStatus() { return this.$store.getters['coins/dataSaveStatus'] },
  },
  watch: {
    saveStatus(val) {
      if (val.status === true) {
        this.showToast({
          variant: 'success', title: 'Başarılı', icon: 'CheckIcon', text: val.message,
        })
      } else if (val.status === false) {
        this.showToast({
          variant: 'danger', title: 'Hata', icon: 'XIcon', text: val.message,
        })
      }
    },
  },
  created() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      await this.$store.dispatch('coins/getDataList', { ...this.dataQuery })
      this.loading = false
    },
    onPageChange(page) {
      this.currentPage = page
      this.dataQuery.page = page
      this.fetchList()
    },
    onFilterChange() {
      this.dataQuery.page = 1
      this.currentPage = 1
      this.fetchList()
    },
    onSearchInput() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.dataQuery.page = 1
        this.currentPage = 1
        this.fetchList()
      }, 400)
    },
    openSyncModal() {
      this.syncModal = { show: true, quoteAssets: this.syncModal.quoteAssets.length ? this.syncModal.quoteAssets : ['USDT'] }
    },
    async onConfirmSync() {
      if (!this.syncModal.quoteAssets.length) return
      this.syncing = true
      const providerKindMap = { 1: 'BINANCE', 2: 'BINANCE_TESTNET', 3: 'SIMULATED' }
      await this.$store.dispatch('coins/syncCoins', {
        providerKind: providerKindMap[this.dataQuery.providerId] || 'BINANCE',
        quoteAssets: [...this.syncModal.quoteAssets],
      })
      this.syncing = false
      this.syncModal.show = false
      this.fetchList()
    },
    // Legacy alias — eski yerlerden çağrılırsa
    async onSync() { this.openSyncModal() },
    async onToggleActive(id, isActive) {
      await this.$store.dispatch('coins/toggleActive', { id, isActive })
      this.fetchList()
    },
    formatDate(date) {
      if (!date) return '-'
      try { return new Date(date).toLocaleString('tr-TR') } catch (e) { return date }
    },
    showToast(params) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: params.title, icon: params.icon, text: params.text, variant: params.variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  100% { transform: rotate(360deg); }
}
</style>
